import type { ArbZgAverageResponse } from '@wf/shared'
import { useTeamArbzgAverage } from './useZeiten'

/**
 * § 3 ArbZG-Durchschnitt für das ganze Team auf einen Blick (docs/DESIGN-zeit-arbzg-durchschnitt.md).
 *
 * Zeigt bewusst NUR die Auffälligen. Der Wert dieser Karte liegt darin, dass sie meistens leer ist: eine
 * Liste, in der jeder steht, liest irgendwann niemand mehr — und dann übersieht man den einen, der zählt.
 * Der vollständige Rechenweg steht je Mitarbeiter im Nachweis.
 */

const h = (sec: number): string => (sec / 3600).toFixed(1).replace('.', ',')

/** Das am stärksten ausgeschöpfte Fenster trägt die Aussage. */
function fuehrend(a: ArbZgAverageResponse) {
  return [...a.guard.windows].sort((x, y) => (y.utilization ?? 0) - (x.utilization ?? 0))[0]
}

export function ArbzgAverageTeamCard({ userIds, names, enabled }: { userIds: readonly number[]; names: ReadonlyMap<number, string>; enabled: boolean }) {
  const results = useTeamArbzgAverage(userIds, enabled)
  if (!enabled || userIds.length === 0) return null

  // ⚠️ Erst „lädt", wenn noch GAR NICHTS da ist. `some(isLoading)` hätte bei zwanzig Mitarbeitern die
  // ganze Karte hinter dem langsamsten Request versteckt.
  const laedt = results.length > 0 && results.every((r) => r.isLoading)
  // ⚠️ Fehlgeschlagene Abfragen dürfen NICHT stillschweigend aus der Liste fallen: dann meldete die Karte
  // „niemand nähert sich der Grenze", obwohl drei Abfragen gescheitert sind — genau die fail-open-
  // Fehlerklasse, gegen die der ganze Wächter gebaut ist.
  const fehlend = results.filter((r) => r.isError).length
  const auffaellig = results
    .map((r) => r.data)
    .filter((d): d is ArbZgAverageResponse => !!d && d.guard.level !== 'gruen')
    .map((d) => ({ d, f: fuehrend(d) }))
    // Schlimmster zuerst — wer die Liste von oben liest, liest das Wichtigste zuerst.
    .sort((a, b) => (b.f?.utilization ?? 0) - (a.f?.utilization ?? 0))

  return (
    <div className="wf-card wf-zscard" aria-label="Acht-Stunden-Durchschnitt im Team">
      <div className="wf-zscard__label">Acht-Stunden-Durchschnitt (§ 3 ArbZG)</div>
      {fehlend > 0 ? (
        <div className="error" role="alert">
          {fehlend} {fehlend === 1 ? 'Mitarbeiter konnte' : 'Mitarbeiter konnten'} nicht geprüft werden — diese Liste ist
          unvollständig.
        </div>
      ) : null}
      {laedt ? (
        <div className="muted">wird geladen…</div>
      ) : auffaellig.length === 0 ? (
        // „Niemand auffällig" nur, wenn auch wirklich JEDER geprüft werden konnte — sonst wäre die
        // Entwarnung eine Lüge, die genau neben der Fehlermeldung stünde.
        fehlend === 0 ? (
          <p className="hint">
            Niemand nähert sich der Grenze. Zulässig sind acht Stunden je Werktag im Durchschnitt des
            Ausgleichszeitraums — die Details je Mitarbeiter stehen im Nachweis.
          </p>
        ) : null
      ) : (
        <ul className="wf-abslist">
          {auffaellig.map(({ d, f }) => (
            <li key={d.user.id} className="wf-abslist__row">
              <span>
                <b>{names.get(d.user.id) ?? d.user.name}</b>{' '}
                {f == null
                  ? 'keine auswertbaren Tage'
                  : f.headroomSeconds >= 0
                    ? `noch ${h(f.headroomSeconds)} h Luft`
                    : `${h(-f.headroomSeconds)} h über der zulässigen Gesamtarbeitszeit`}
                {f?.partial ? <span className="hint"> (Zwischenstand)</span> : null}
              </span>
              <span className={d.guard.level === 'rot' ? 'wf-tag wf-abs--abgelehnt' : 'wf-tag'}>
                {f?.utilization == null ? '—' : `${Math.round(f.utilization * 100)} %`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
